import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Spinner from '../components/ui/Spinner';
import MultiSelectDropdown from '../components/ui/MultiSelectDropdown';
import { getBranches } from '../services/branches';
import { getDrivers } from '../services/drivers';
import { exportDriverExpenses, getDriverExpenses } from '../services/driver_expenses';
import { USER_PROFILE, USER_ROLE } from '../services/auth';
import { formatDMY } from '../utils/datetime';

const DriverExpenseReport = () => {
  const role = localStorage.getItem(USER_ROLE);
  const profile = JSON.parse(localStorage.getItem(USER_PROFILE) || '{}');
  const isAdmin = role === 'admin';

  const currentYear = new Date().getFullYear();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(currentYear);
  const [selectedBranch, setSelectedBranch] = useState(0);
  const [selectedDrivers, setSelectedDrivers] = useState([]);

  const [branches, setBranches] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [rows, setRows] = useState([]);

  const effectiveBranch = isAdmin ? selectedBranch : (profile?.branch?.id || 0);

  const monthOptions = useMemo(() => [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ], []);

  const yearOptions = useMemo(() => {
    return Array.from({ length: 7 }, (_, idx) => currentYear - 5 + idx);
  }, [currentYear]);

  useEffect(() => {
    const loadBranches = async () => {
      if (!isAdmin) return;
      setLoadingFilters(true);
      const [err, data] = await getBranches({ status: 'active', paginate: false });
      if (!err) {
        setBranches(Array.isArray(data) ? data : (data?.results || []));
      }
      setLoadingFilters(false);
    };
    loadBranches();
  }, [isAdmin]);

  useEffect(() => {
    const loadDrivers = async () => {
      setLoadingFilters(true);
      const [err, data] = await getDrivers({
        status: 'active',
        branch: effectiveBranch,
        paginate: false,
      });
      if (!err) {
        const allDrivers = (Array.isArray(data) ? data : (data?.results || []))
          .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
        setDrivers(allDrivers);
        setSelectedDrivers(prev => prev.filter(id => allDrivers.some(d => d.id === id)));
      }
      setLoadingFilters(false);
    };
    loadDrivers();
  }, [effectiveBranch]);

  const validate = () => {
    if (selectedDrivers.length === 0) {
      toast.error('Select at least one driver.');
      return false;
    }
    return true;
  };

  const fetchViewData = async () => {
    const [err, data] = await getDriverExpenses({
      drivers: selectedDrivers.join(','),
      month,
      year,
      branch: effectiveBranch,
      paginate: false,
    });
    if (err) return [];
    return Array.isArray(data) ? data : (data?.results || []);
  };

  const handleView = async () => {
    if (!validate()) return;

    setLoadingTable(true);
    const merged = await fetchViewData();

    setRows(merged);
    setLoadingTable(false);
  };

  const handleDownload = async () => {
    if (!validate()) return;

    const response = await exportDriverExpenses({
      month,
      year,
      branch: effectiveBranch,
      drivers: selectedDrivers.join(','),
      format: 'xls',
    });
    if (!response || Array.isArray(response)) {
      toast.error('Failed to download report.');
      return;
    }

    const blobUrl = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `driver-expense-report-${month}-${year}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className="p-4 w-full bg-slate-50 min-h-screen uppercase [&_select]:uppercase [&_input[type='text']]:uppercase [&_input:disabled]:uppercase">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-bold uppercase text-[#293686]">Driver Expense Report</h1>
        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#E8ECFF] text-[#293686] border border-[#D8E0FF]">
          Selected Drivers: {selectedDrivers.length}
        </span>
      </div>

      <div className="bg-white border border-[#D8E0FF] rounded-xl shadow-sm p-4 md:p-5">
        <div className="grid md:grid-cols-5 gap-4 items-end">
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Month</label>
            <select className="border rounded p-2 text-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {monthOptions.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Year</label>
            <select className="border rounded p-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Branch</label>
              <select
                className="border rounded p-2 text-sm"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(Number(e.target.value))}
              >
                <option value={0}>All Branches</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
            </div>
          )}
          {!isAdmin && (
            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Branch</label>
              <input
                type="text"
                readOnly
                value={profile?.branch?.name || '-'}
                className="border rounded p-2 text-sm bg-gray-50 text-gray-700"
              />
            </div>
          )}

          <div className="md:col-span-2">
            <MultiSelectDropdown
              label="Driver"
              placeholder="Select drivers"
              options={drivers}
              selectedValues={selectedDrivers}
              onChange={setSelectedDrivers}
              getOptionLabel={(driver) => (driver?.name || '-').toUpperCase()}
              getOptionValue={(driver) => driver?.id}
              showSelectAll={true}
              selectAllLabel="All Drivers"
              disabled={loadingFilters}
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button className="bg-[#293686] hover:bg-[#1F2968] text-white px-4 py-2 rounded text-sm font-medium" onClick={handleView}>View</button>
          <button className="bg-[#0F9D58] hover:bg-[#0B7D46] text-white px-4 py-2 rounded text-sm font-medium" onClick={handleDownload}>Download</button>
        </div>
      </div>

      <div className="overflow-x-auto mt-4 rounded-xl border border-[#D8E0FF] bg-white shadow-sm">
        <table className="min-w-full">
          <thead className="bg-[#293686] text-white uppercase text-sm">
            <tr>
              <th className="px-4 py-2 border text-center">Driver</th>
              <th className="px-4 py-2 border text-center">Date</th>
              <th className="px-4 py-2 border text-center">Charges</th>
              <th className="px-4 py-2 border text-center">Amount</th>
              <th className="px-4 py-2 border text-center">Reason</th>
              <th className="px-4 py-2 border text-center">Remarks</th>
              <th className="px-4 py-2 border text-center">Attachment</th>
              <th className="px-4 py-2 border text-center">Created By</th>
              <th className="px-4 py-2 border text-center">Confirmed By</th>
              <th className="px-4 py-2 border text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {(loadingFilters || loadingTable) ? (
              <tr>
                <td colSpan="10" className="text-center py-4"><Spinner /></td>
              </tr>
            ) : rows.length > 0 ? (
              rows.map(expense => (
                <tr key={expense.id} className="text-sm text-gray-800">
                  <td className="px-4 py-2 border text-center">{expense.driver?.name || '-'}</td>
                  <td className="px-4 py-2 border text-center">{expense.date ? formatDMY(expense.date) : '-'}</td>
                  <td className="px-4 py-2 border text-center">{expense.expense_type?.toUpperCase() || '-'}</td>
                  <td className="px-4 py-2 border text-center">{expense.amount ? `₹${expense.amount}` : '-'}</td>
                  <td className="px-4 py-2 border text-center">{expense.reason || '-'}</td>
                  <td className="px-4 py-2 border text-center">{expense.remarks || '-'}</td>
                  <td className="px-4 py-2 border text-center">
                    {expense.supporting_doc ? (
                      <a href={expense.supporting_doc} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">VIEW</a>
                    ) : '-'}
                  </td>
                  <td className="px-4 py-2 border text-center">{expense.created_by?.name?.toUpperCase() || '-'}</td>
                  <td className="px-4 py-2 border text-center">{expense.confirmed_by?.name?.toUpperCase() || 'SELF'}</td>
                  <td className="px-4 py-2 border text-center">{expense.status?.toUpperCase() || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center text-gray-500 py-3">NO EXPENSE DATA FOUND.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DriverExpenseReport;
